import {
  getAuthenticatedWsUrl,
  getBaseSummarySessionWsUrl,
} from "./wsUrlUtils";
import * as Sentry from "@sentry/react";

export type EphemeralRequestState =
  | "connecting"
  | "streaming"
  | "finished"
  | "canceled"
  | "error";

export interface EphemeralWsRequestOptions<TPayload> {
  url: string; // already includes ws:// or wss://
  payload: TPayload;
  timeoutMs?: number; // abort if no bytes in N ms
}

/**
 * EphemeralWsRequest - One WebSocket connection per request
 *
 * This class creates a dedicated WebSocket connection for a single request/response cycle.
 * The connection automatically closes when:
 * - Server sends finished_stream: true
 * - Timeout is reached
 * - Client calls cancel()
 * - Connection error occurs (and reconnection attempts are exhausted)
 */
// Utility to generate UUIDs compatible with older browsers / Android WebViews
function generateUUID(): string {
  if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
    return (crypto as any).randomUUID();
  }
  // Fallback RFC-4122 v4 compliant polyfill
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class EphemeralWsRequest<TIn = unknown, TOut = unknown> {
  /* — public surface — */
  readonly id = generateUUID();
  state: EphemeralRequestState = "connecting";
  lastMessage: TOut | null = null;
  readonly messages: TOut[] = [];

  /** Subscribe to *this* request only. */
  subscribe(cb: (req: EphemeralWsRequest<TIn, TOut>) => void): () => void {
    this._subs.add(cb);
    return () => this._subs.delete(cb);
  }
  cancel = () => {
    this._aborted = true;
    this._finish("canceled", "client_cancelled");
  };

  /* — private — */
  private ws: WebSocket;
  private readonly _subs = new Set<
    (r: EphemeralWsRequest<TIn, TOut>) => void
  >();
  private timeoutHandle?: ReturnType<typeof setTimeout>;
  private connectTimeoutHandle?: ReturnType<typeof setTimeout>;
  private _aborted = false; // set by cancel()
  errorReason?: string; // public surface for caller

  // --- reconnection helpers ---
  private _url: string;
  private _payload: TIn;
  private _timeoutMs?: number;
  private _reconnectAttempts = 0;
  private readonly _maxReconnectAttempts = 0; // Currently set to 0, since we likely need a backend infra that let's us avoid duplicate requests (e.g. via a lock and globally connectable/cancellable workers/units) which will fix the issue of duplicated requests contending/collisioning over the session/DB record. Also needs to make the frontend handle it's stream state reconciliation (or reset it with the newly rehydrated state)

  constructor({ url, payload, timeoutMs }: EphemeralWsRequestOptions<TIn>) {
    // Store params for potential reconnection attempts
    this._url = url;
    this._payload = payload;
    this._timeoutMs = timeoutMs;

    // Initialize as an empty websocket; will be created in _connect
    this.ws = null as unknown as WebSocket;

    // Kick-off first connection
    this._connect();
  }

  private _notify() {
    this._subs.forEach((cb) => cb(this));
  }

  private _finish(to: EphemeralRequestState, reason?: string) {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }
    if (
      this.state !== "finished" &&
      this.state !== "canceled" &&
      this.state !== "error"
    ) {
      this.state = to;
      if (reason) this.errorReason = reason;
      // Only try to close the WebSocket if it exists
      if (this.ws) {
        this.ws.close();
      }
      this._notify();
    }
  }

  private _keepAlive(ms: number) {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }
    this.timeoutHandle = setTimeout(() => this._finish("error", "timeout"), ms);
  }

  /**
   * Establishes a WebSocket connection. Re-used for initial connect and retries.
   */
  private _connect() {
    if (this._aborted) return; // do not reconnect if explicitly cancelled

    getAuthenticatedWsUrl(this._url)
      .then((fullUrl) => {
        // If we're retrying, surface that we are reconnecting
        if (this.state === "streaming" || this.state === "connecting") {
          this.state = "connecting";
          this._notify();
        }

        this.ws = new WebSocket(fullUrl);

        // handshake watchdog – if onopen isn’t reached within 10s, treat as failed
        if (this.connectTimeoutHandle) clearTimeout(this.connectTimeoutHandle);
        this.connectTimeoutHandle = setTimeout(() => {
          if (this.state === "connecting") {
            this.errorReason = "handshake_timeout";
            this._handleClose();
          }
        }, 10000);

        this.ws.onopen = () => {
          if (this._aborted) {
            this.ws.close();
            return;
          }
          if (this.connectTimeoutHandle)
            clearTimeout(this.connectTimeoutHandle);
          this.ws.send(JSON.stringify(this._payload));
          // Successful connection; reset reconnect attempt counter
          this._reconnectAttempts = 0;
          this.state = "streaming";
          this._notify();
          if (this._timeoutMs) {
            this._keepAlive(this._timeoutMs);
          }
        };

        this.ws.onmessage = (ev) => {
          if (this._timeoutMs) {
            this._keepAlive(this._timeoutMs);
          }
          
          let msg: TOut;
          try {
            msg = JSON.parse(ev.data) as TOut;
          } catch (parseError) {
            console.error("❌ Failed to parse WebSocket message::", parseError, "Raw data:", ev.data);
            
            // Capture detailed error information in Sentry
            Sentry.captureException(parseError, {
              level: "error",
              tags: {
                error_type: "websocket_json_parse_error",
                ws_state: this.state,
              },
              contexts: {
                websocket: {
                  url: this._url,
                  request_id: this.id,
                  reconnect_attempts: this._reconnectAttempts,
                },
                response: {
                  raw_data_preview: typeof ev.data === 'string' 
                    ? ev.data.substring(0, 500) 
                    : String(ev.data).substring(0, 500),
                  raw_data_length: typeof ev.data === 'string' 
                    ? ev.data.length 
                    : String(ev.data).length,
                  data_type: typeof ev.data,
                },
              },
              extra: {
                full_raw_response: ev.data,
                payload_sent: this._payload,
              },
            });
            
            // Show connection error modal to user
            import("@sharpana/shared/stores/connectionErrorModalStore").then((module) => {
              const { useConnectionErrorModalStore } = module;
              useConnectionErrorModalStore.getState().show();
            });
            this._finish("error", "invalid_json_response");
            return;
          }
          
          this.lastMessage = msg;
          this.messages.push(msg);
          this._notify();
          if ((msg as any).response_type === "invitation_to_create_account") {
            // Trigger the global app store to show the invitation modal
            import("@sharpana/shared/stores/globalAppStore").then((module) => {
              const { useGlobalAppStore } = module;
              useGlobalAppStore.getState().setInvitationPayload((msg as any).payload);
              useGlobalAppStore.getState().setCurrentPagePath(window.location.pathname);
              useGlobalAppStore.getState().setShowAccountInvitationModal(true);
            });
            this._finish("finished");
          }
          // Centralized handling for insufficient tokens
          if ((msg as any).response_type === "error" && (msg as any).error_type === "insufficient_tokens") {
            const m: any = msg as any;
            import("@sharpana/shared/stores/globalAppStore").then((module) => {
              const { useGlobalAppStore } = module;
              useGlobalAppStore.getState().setInsufficientTokensHasActiveSubscription(!!m.has_active_subscription);
              useGlobalAppStore.getState().setShowInsufficientTokensModal(true);
            });
            this._finish("error", "insufficient_tokens");
            return;
          }
          if ((msg as any).finished_stream === true) {
            this._finish("finished");
          }
        };

        // Error / Close handling
        this.ws.onerror = (ev: Event | any) => {
          this.errorReason = (ev?.message ?? "ws_error") as string;
          this._handleClose();
        };
        this.ws.onclose = (ev) => this._handleClose(ev);
      })
      .catch((err) => {
        console.error("Failed to authenticate WebSocket URL:", err);
        this._finish("error", this.errorReason ?? "unknown_error");
      });
  }

  /**
   * Decide whether we should attempt a reconnection or mark the request as errored.
   */
  private _handleClose(ev?: CloseEvent) {
    if (this.connectTimeoutHandle) clearTimeout(this.connectTimeoutHandle);

    // Normal closure (1000) when we already finished: treat as finished
    if (ev?.code === 1000) {
      if (this.state === "finished" || this.state === "canceled") return; // already final
      this._finish("finished", "server_closed_cleanly");
      return;
    }

    if (
      (this.state === "streaming" || this.state === "connecting") &&
      this._reconnectAttempts < this._maxReconnectAttempts
    ) {
      const delay = Math.min(
        Math.pow(2, this._reconnectAttempts) * 1000,
        10000
      );
      this._reconnectAttempts += 1;
      setTimeout(() => this._connect(), delay);
    } else if (
      (this.state === "streaming" || this.state === "connecting") &&
      this._reconnectAttempts >= this._maxReconnectAttempts
    ) {
      // Show connection error modal
      import("@sharpana/shared/stores/connectionErrorModalStore").then((module) => {
        const { useConnectionErrorModalStore } = module;
        useConnectionErrorModalStore.getState().show();
      });
      this._finish(
        "error",
        this.errorReason ?? "max_reconnect_attempts_exceeded"
      );
    } else {
      this._finish("error", this.errorReason ?? "unknown_error");
    }
  }
}

export function startEphemeralWsRequest<TIn, TOut>(
  opts: EphemeralWsRequestOptions<TIn>
): EphemeralWsRequest<TIn, TOut> {
  return new EphemeralWsRequest<TIn, TOut>(opts);
}

/**
 * Creates an ephemeral WebSocket request with the base URL for the current environment
 * @param payload The payload to send in the request
 * @param timeoutMs The timeout in milliseconds
 * @returns The created ephemeral WebSocket request
 */
export function startEphemeralSummarySessionRequest<TIn, TOut>(
  payload: TIn,
  timeoutMs?: number
): EphemeralWsRequest<TIn, TOut> {
  return startEphemeralWsRequest<TIn, TOut>({
    url: getBaseSummarySessionWsUrl(),
    payload: payload,
    timeoutMs: timeoutMs ? timeoutMs : 90000,
  });
}
